import {
  httpGet,
  httpPost,
  httpPut,
  type HttpRequestConfig,
} from "src-next/shared/api/httpClient";

import type {
  WooCreateOrderPayload,
  WooCustomer,
  WooOrder,
  WooOrderLineItemDetail,
  WooProduct,
  WooStoreConfig,
} from "../types";
import { resolveWordPressSiteBase } from "../utils/wordpressSiteBase";

const WOO_ADAPTER_BASE = "/adapters/woocommerce";

/** Resposta bruta WooCommerce REST API v3 — Products. */
interface WooProductRaw {
  id: number;
  name: string;
  sku: string;
  price: string;
  regular_price: string;
  sale_price: string;
  on_sale: boolean;
  images?: { id: number; src: string; thumbnail?: string; name?: string }[];
  stock_status: string;
  purchasable: boolean;
  manage_stock?: boolean;
  stock_quantity?: number | null;
  backorders?: string;
  short_description: string;
  categories?: { id: number; name: string }[];
  tags?: { id: number; name: string }[];
}

/** Resposta bruta WooCommerce REST API v3 — Customers. */
interface WooCustomerRaw {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  billing: WooCustomer["billing"];
  shipping: WooCustomer["shipping"];
}

interface WooOrderLineItemRaw {
  id: number;
  product_id: number;
  name: string;
  quantity: number;
  price: number;
  subtotal: string;
  total: string;
  image?: { src?: string };
}

/** Resposta bruta WooCommerce REST API v3 — Orders. */
interface WooOrderRaw {
  id: number;
  order_key: string;
  status: string;
  total: string;
  currency: string;
  date_created: string;
  date_paid: string | null;
  payment_url?: string;
  customer_id: number;
  needs_payment?: boolean;
  payment_method?: string;
  meta_data?: Array<{ id?: number; key: string; value: string }>;
  line_items?: WooOrderLineItemRaw[];
  billing?: WooCustomer["billing"];
  shipping?: WooCustomer["shipping"];
}

function adapterConfig(store: WooStoreConfig): HttpRequestConfig {
  const targetOrganization = store.institutionId?.trim();
  if (!targetOrganization) {
    throw new Error("Dispensador sem CNPJ (X-Target-Organization) configurado.");
  }

  return {
    headers: { "X-Target-Organization": targetOrganization },
  };
}

function buildPaymentUrl(store: WooStoreConfig, raw: WooOrderRaw): string {
  if (raw.payment_url) return raw.payment_url;
  const origin = resolveWordPressSiteBase(store.siteOrigin);
  return `${origin}/finalizar-compra/order-pay/${raw.id}/?pay_for_order=true&key=${raw.order_key}`;
}

function mapLineItem(raw: WooOrderLineItemRaw): WooOrderLineItemDetail {
  return {
    id: raw.id,
    productId: raw.product_id,
    name: raw.name,
    quantity: raw.quantity,
    price: raw.price,
    subtotal: raw.subtotal,
    total: raw.total,
    imageSrc: raw.image?.src,
  };
}

function mapOrder(raw: WooOrderRaw, store: WooStoreConfig): WooOrder {
  return {
    id: raw.id,
    orderKey: raw.order_key,
    status: raw.status,
    total: raw.total ?? "0",
    currency: raw.currency ?? "BRL",
    dateCreated: raw.date_created,
    datePaid: raw.date_paid,
    paymentUrl: buildPaymentUrl(store, raw),
    customerId: raw.customer_id,
    needsPayment: Boolean(raw.needs_payment),
    lineItems: (raw.line_items ?? []).map(mapLineItem),
    shipping: raw.shipping,
    billing: raw.billing,
    storeId: store.id,
    storeName: store.name,
    paymentMethod: raw.payment_method,
    metaData: (raw.meta_data ?? []).map((entry) => ({
      key: entry.key,
      value: String(entry.value ?? ""),
    })),
  };
}

function mapProduct(raw: WooProductRaw): WooProduct {
  return {
    id: raw.id,
    name: raw.name,
    sku: raw.sku ?? "",
    price: raw.price ?? raw.regular_price ?? "0",
    regularPrice: raw.regular_price ?? "0",
    salePrice: raw.sale_price ?? "",
    onSale: Boolean(raw.on_sale),
    images: (raw.images ?? []).map((img) => ({
      id: img.id,
      src: img.src,
      thumbnail: img.thumbnail,
      name: img.name,
    })),
    stockStatus: raw.stock_status ?? "instock",
    purchasable: raw.purchasable !== false,
    manageStock: Boolean(raw.manage_stock),
    stockQuantity:
      raw.stock_quantity === undefined || raw.stock_quantity === null
        ? null
        : Number(raw.stock_quantity),
    backordersAllowed: (raw.backorders ?? "no") !== "no",
    shortDescription: raw.short_description ?? "",
    categories: raw.categories ?? [],
    tags: raw.tags ?? [],
  };
}

function mapCustomer(raw: WooCustomerRaw): WooCustomer {
  return {
    id: raw.id,
    email: raw.email,
    first_name: raw.first_name ?? "",
    last_name: raw.last_name ?? "",
    billing: raw.billing ?? ({} as WooCustomer["billing"]),
    shipping: raw.shipping ?? ({} as WooCustomer["shipping"]),
  };
}

async function listProducts(
  store: WooStoreConfig,
  search?: string,
): Promise<WooProduct[]> {
  const params: Record<string, string | number> = {
    per_page: 100,
    page: 1,
    status: "publish",
  };
  if (search?.trim()) {
    params.search = search.trim();
  }

  const data = await httpGet<WooProductRaw[]>(`${WOO_ADAPTER_BASE}/products`, {
    ...adapterConfig(store),
    params,
  });

  return (data ?? []).map(mapProduct);
}

async function listCustomers(store: WooStoreConfig): Promise<WooCustomer[]> {
  const data = await httpGet<WooCustomerRaw[]>(`${WOO_ADAPTER_BASE}/customers`, {
    ...adapterConfig(store),
    params: { role: "all", per_page: 100 },
  });

  return (data ?? []).map(mapCustomer);
}

async function findCustomerByEmail(
  store: WooStoreConfig,
  email: string,
): Promise<WooCustomer | null> {
  const normalized = email.trim().toLowerCase();
  if (!normalized) return null;

  const data = await httpGet<WooCustomerRaw[]>(`${WOO_ADAPTER_BASE}/customers`, {
    ...adapterConfig(store),
    params: { email: normalized, role: "all", per_page: 1 },
  });

  const match = (data ?? []).find(
    (c) => c.email.trim().toLowerCase() === normalized,
  );
  return match ? mapCustomer(match) : null;
}

async function getCustomerById(
  store: WooStoreConfig,
  customerId: number,
): Promise<WooCustomer> {
  const data = await httpGet<WooCustomerRaw>(
    `${WOO_ADAPTER_BASE}/customers/${customerId}`,
    adapterConfig(store),
  );

  return mapCustomer(data);
}

async function updateCustomer(
  store: WooStoreConfig,
  customerId: number,
  payload: Partial<WooCustomerRaw>,
): Promise<WooCustomer> {
  const data = await httpPut<WooCustomerRaw, Partial<WooCustomerRaw>>(
    `${WOO_ADAPTER_BASE}/customers/${customerId}`,
    payload,
    adapterConfig(store),
  );

  return mapCustomer(data);
}

async function listOrdersByCustomer(
  store: WooStoreConfig,
  customerId: number,
  options?: { after?: string; perPage?: number },
): Promise<WooOrder[]> {
  const params: Record<string, string | number> = {
    customer: customerId,
    per_page: options?.perPage ?? 50,
    orderby: "date",
    order: "desc",
  };
  if (options?.after) {
    params.after = options.after;
  }

  const data = await httpGet<WooOrderRaw[]>(`${WOO_ADAPTER_BASE}/orders`, {
    ...adapterConfig(store),
    params,
  });

  return (data ?? []).map((raw) => mapOrder(raw, store));
}

const ESCALATING_WINDOW_MONTHS = [3, 6, 12] as const;

function subtractMonthsIso(months: number): string {
  const date = new Date();
  date.setMonth(date.getMonth() - months);
  return date.toISOString();
}

/** Busca pedidos com janela escalonada: 3 → 6 → 12 meses. */
async function listOrdersByCustomerWithEscalatingWindow(
  store: WooStoreConfig,
  customerId: number,
): Promise<WooOrder[]> {
  for (const months of ESCALATING_WINDOW_MONTHS) {
    const orders = await listOrdersByCustomer(store, customerId, {
      after: subtractMonthsIso(months),
      perPage: 100,
    });
    if (orders.length > 0) return orders;
  }
  return [];
}

const CHECKOUT_ORDERS_WINDOW_MONTHS = 3;

/** Pedidos recentes para prefilling de endereço no checkout (janela fixa). */
async function listRecentOrdersForCheckout(
  store: WooStoreConfig,
  customerId: number,
): Promise<WooOrder[]> {
  return listOrdersByCustomer(store, customerId, {
    after: subtractMonthsIso(CHECKOUT_ORDERS_WINDOW_MONTHS),
    perPage: 100,
  });
}

async function getOrder(
  store: WooStoreConfig,
  orderId: number,
): Promise<WooOrder> {
  const data = await httpGet<WooOrderRaw>(
    `${WOO_ADAPTER_BASE}/orders/${orderId}`,
    adapterConfig(store),
  );

  return mapOrder(data, store);
}

export interface UpdateOrderPayload {
  line_items?: Array<{
    id: number;
    product_id: number;
    quantity: number;
  }>;
  status?: string;
}

async function updateOrder(
  store: WooStoreConfig,
  orderId: number,
  payload: UpdateOrderPayload,
): Promise<WooOrder> {
  const data = await httpPut<WooOrderRaw, UpdateOrderPayload>(
    `${WOO_ADAPTER_BASE}/orders/${orderId}`,
    payload,
    adapterConfig(store),
  );

  return mapOrder(data, store);
}

async function createOrder(
  store: WooStoreConfig,
  payload: WooCreateOrderPayload,
): Promise<WooOrder> {
  const data = await httpPost<WooOrderRaw, WooCreateOrderPayload>(
    `${WOO_ADAPTER_BASE}/orders`,
    payload,
    adapterConfig(store),
  );

  return getOrder(store, data.id);
}

function filterProductsLocally(
  products: WooProduct[],
  searchTerm: string,
): WooProduct[] {
  const term = searchTerm.trim().toLowerCase();
  if (!term) return products;

  return products.filter((product) => {
    const inName = product.name.toLowerCase().includes(term);
    const inSku = product.sku.toLowerCase().includes(term);
    const inCategory = product.categories.some((c) =>
      c.name.toLowerCase().includes(term),
    );
    const inTag = product.tags.some((t) =>
      t.name.toLowerCase().includes(term),
    );
    return inName || inSku || inCategory || inTag;
  });
}

/**
 * IO WooCommerce via adapter APIM — produtos, customers e pedidos.
 * Autenticação Fluxmed (Bearer) + header X-Target-Organization (CNPJ cru) por dispensador.
 */
export const woocommerceStoreService = {
  listProducts,
  listCustomers,
  findCustomerByEmail,
  getCustomerById,
  updateCustomer,
  listOrdersByCustomer,
  listOrdersByCustomerWithEscalatingWindow,
  listRecentOrdersForCheckout,
  getOrder,
  updateOrder,
  createOrder,
  filterProductsLocally,
};

export type { WooProductRaw, WooCustomerRaw, WooOrderRaw };
